// React Query hooks for API operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, apiKeys } from '../lib/api'
import { 
  CreateContentRequest, 
  UpdateContentRequest, 
  GenerateContentRequest,
  PublishContentRequest,
  AnalyticsRequest 
} from '../../shared/types'

// Pitches hooks
export function usePitches() {
  return useQuery({
    queryKey: apiKeys.pitches(),
    queryFn: () => apiClient.getPitches(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  })
}

export function useGenerateNewPitches() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: () => apiClient.generateNewPitches(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeys.pitches() })
    }
  })
}

// Content hooks
export function useContent() {
  return useQuery({
    queryKey: apiKeys.content(),
    queryFn: () => apiClient.getContent(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

export function useContentItem(id: string) {
  return useQuery({
    queryKey: apiKeys.contentItem(id),
    queryFn: () => apiClient.getContentItem(id),
    enabled: !!id
  })
}

export function useCreateContent() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: CreateContentRequest) => apiClient.createContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeys.content() })
    }
  })
}

export function useUpdateContent() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateContentRequest }) => 
      apiClient.updateContent(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: apiKeys.content() })
      queryClient.invalidateQueries({ queryKey: apiKeys.contentItem(id) })
    }
  })
}

export function useGenerateContent() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: GenerateContentRequest) => apiClient.generateContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeys.content() })
    }
  })
}

// Social media hooks
export function useSocialAccounts() {
  return useQuery({
    queryKey: apiKeys.socialAccounts(),
    queryFn: () => apiClient.getSocialAccounts(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  })
}

export function useConnectSocialAccount() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ platform, code, state }: { platform: string; code: string; state: string }) => 
      apiClient.connectSocialAccount(platform, code, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeys.socialAccounts() })
    }
  })
}

export function usePublishContent() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: PublishContentRequest) => apiClient.publishContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiKeys.content() })
    }
  })
}

// Analytics hooks
export function useAnalytics(params: AnalyticsRequest = {}) {
  return useQuery({
    queryKey: apiKeys.analytics(params),
    queryFn: () => apiClient.getAnalytics(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useEngagementData(period: string = '30d') {
  return useQuery({
    queryKey: [...apiKeys.analytics(), 'engagement', period],
    queryFn: () => apiClient.getEngagementData(period),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export function useContentPerformance() {
  return useQuery({
    queryKey: [...apiKeys.analytics(), 'performance'],
    queryFn: () => apiClient.getContentPerformance(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// User hooks
export function useCurrentUser() {
  return useQuery({
    queryKey: apiKeys.user(),
    queryFn: () => apiClient.getCurrentUser(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false,
    refetchOnWindowFocus: false
  })
}

// Generic hooks for common patterns
export function useOptimisticUpdate<T>(
  queryKey: readonly unknown[],
  updateFn: (oldData: T | undefined, newData: any) => T
) {
  const queryClient = useQueryClient()
  
  return {
    onMutate: async (newData: any) => {
      await queryClient.cancelQueries({ queryKey })
      const previousData = queryClient.getQueryData<T>(queryKey)
      
      queryClient.setQueryData<T>(queryKey, (oldData) => 
        updateFn(oldData, newData)
      )
      
      return { previousData }
    },
    onError: (err: any, newData: any, context: any) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    }
  }
}

// Infinite query hook for paginated data
export function useInfiniteContent(filters?: any) {
  return useQuery({
    queryKey: ['content', 'infinite', filters],
    queryFn: ({ pageParam = 1 }) => 
      apiClient.request(`/content?page=${pageParam}&limit=20${filters ? `&${new URLSearchParams(filters).toString()}` : ''}`),
    // getNextPageParam: (lastPage, pages) => {
    //   return lastPage.pagination?.hasNext ? pages.length + 1 : undefined
    // },
    staleTime: 2 * 60 * 1000,
  })
}

// Real-time updates hook (for WebSocket or polling)
export function useRealTimeUpdates(enabled: boolean = true) {
  const queryClient = useQueryClient()
  
  // This would implement WebSocket connections or polling for real-time updates
  // For now, we'll just implement polling for demo purposes
  
  return useQuery({
    queryKey: ['realtime'],
    queryFn: () => Promise.resolve(null),
    enabled,
    refetchInterval: enabled ? 30 * 1000 : false, // Poll every 30 seconds
    onSuccess: () => {
      // Invalidate relevant queries when we get updates
      queryClient.invalidateQueries({ queryKey: apiKeys.content() })
      queryClient.invalidateQueries({ queryKey: apiKeys.pitches() })
    }
  })
}